import React from 'react';
import moment from 'moment';
import { Typography } from '@mui/material';
import {
  getDigitadoEmValue,
  getStatusDigitacaoLabel,
  getSubNsmpStatus,
} from '../../../../helpers/usoMutuoDigitacaoStatus';
import {
  getUsoMutuoRowKind,
  USO_MUTUO_ROW_KIND,
} from './nsmpRowTypes';
import { isSemSucata } from './usoMutuoSucataSchema';

const LabelValue = ({ label, value, valueVariant = 'caption', labelVariant = 'caption' }) => {
  if (value == null || value === '') return null;
  return (
    <>
      <Typography variant={labelVariant} fontWeight={700} component="span">{label}: </Typography>
      <Typography variant={valueVariant} component="span">{value}</Typography>
    </>
  );
};

const StatusLabelValue = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <>
      <Typography variant="body1" fontWeight={700} component="span">{label}: </Typography>
      <Typography variant="body1" component="span">{value}</Typography>
    </>
  );
};

const EmptyCell = () => <Typography variant="body2">-</Typography>;

export const formatUsoMutuoDate = (value) => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

export const formatPesoKg = (pesoTotalKg) => {
  if (pesoTotalKg == null || pesoTotalKg === '') return '';
  const numeric = Number(pesoTotalKg);
  if (Number.isNaN(numeric)) return '';
  return `${numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
};

const formatPeriodoRange = (sucata) => {
  const inicio = formatUsoMutuoDate(sucata?.periodoInicio);
  const fim = formatUsoMutuoDate(sucata?.periodoFim);
  if (inicio && fim) return `${inicio} - ${fim}`;
  return inicio || fim || '';
};

const isMarcadorRow = (kind) =>
  kind === USO_MUTUO_ROW_KIND.ANALISE
  || kind === USO_MUTUO_ROW_KIND.FOTO_REGISTRO
  || kind === USO_MUTUO_ROW_KIND.EVIDENCIA_CAMPO;

const isSucataRow = (kind) =>
  kind === USO_MUTUO_ROW_KIND.ENTREGA_SUCATA || kind === USO_MUTUO_ROW_KIND.PESAGEM_SUCATA;

export const renderUsoMutuoItemCell = (row) => {
  const kind = getUsoMutuoRowKind(row);

  if (kind === USO_MUTUO_ROW_KIND.POSTE) {
    return (
      <>
        <Typography variant="body2">ID {row.numeroPoste}</Typography>
        {row.endereco && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>{row.endereco}</Typography>
        )}
      </>
    );
  }

  if (kind === USO_MUTUO_ROW_KIND.ANALISE || kind === USO_MUTUO_ROW_KIND.EVIDENCIA_CAMPO) {
    const data = row.criadoEmValue || formatUsoMutuoDate(row.criadoEm);
    const titulo = kind === USO_MUTUO_ROW_KIND.EVIDENCIA_CAMPO
      ? 'Evidência de campo'
      : 'Análise de Risco';
    return (
      <>
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.25 }}>
          {titulo}
        </Typography>
        {data && (
          <LabelValue label="Data" value={data} valueVariant="body2" labelVariant="body2" />
        )}
      </>
    );
  }

  if (kind === USO_MUTUO_ROW_KIND.FOTO_REGISTRO) {
    return (
      <Typography variant="body2">Termo Responsabilidade Sucata</Typography>
    );
  }

  if (isSucataRow(kind)) {
    const titulo = kind === USO_MUTUO_ROW_KIND.ENTREGA_SUCATA
      ? 'Entrega Sucata'
      : 'Pesagem Sucata';
    const data = formatUsoMutuoDate(row.sucata?.data);
    return (
      <>
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.25 }}>
          {titulo}
        </Typography>
        {data && (
          <LabelValue label="Data" value={data} valueVariant="body2" labelVariant="body2" />
        )}
      </>
    );
  }

  return null;
};

export const renderUsoMutuoCoordenadaCell = (row) => {
  const kind = getUsoMutuoRowKind(row);

  if (kind === USO_MUTUO_ROW_KIND.POSTE) {
    return (
      <>
        <LabelValue label="Latitude" value={row.latitude} />
        {row.latitude != null && row.longitude != null && <br />}
        <LabelValue label="Longitude" value={row.longitude} />
      </>
    );
  }

  if (isMarcadorRow(kind) || isSucataRow(kind)) {
    return <EmptyCell />;
  }

  return null;
};

export const renderUsoMutuoSituacaoCell = (row) => {
  const kind = getUsoMutuoRowKind(row);

  if (kind === USO_MUTUO_ROW_KIND.POSTE) {
    return (
      <>
        <Typography variant="body2">{row.situacaoPoste}</Typography>
        {(row.situacaoPoste === 'Não compartilhável' || row.situacaoPoste === 'Sem irregularidade') && (
          <>
            <Typography variant="caption" fontWeight={700}>Obs.: </Typography>
            <Typography variant="caption">{row.observacao}</Typography>
          </>
        )}
      </>
    );
  }

  if (isSucataRow(kind)) {
    const periodo = formatPeriodoRange(row.sucata);
    if (!periodo) return <EmptyCell />;

    return (
      <>
        <Typography variant="body2">Período:</Typography>
        <Typography variant="caption" fontWeight={700}>{periodo}</Typography>
      </>
    );
  }

  if (isMarcadorRow(kind)) {
    return <EmptyCell />;
  }

  return null;
};

export const renderUsoMutuoStatusCell = (row) => {
  const kind = getUsoMutuoRowKind(row);

  if (kind === USO_MUTUO_ROW_KIND.POSTE) {
    return renderUsoMutuoStatusDigitacao(row);
  }

  if (isSucataRow(kind)) {
    if (kind === USO_MUTUO_ROW_KIND.PESAGEM_SUCATA && isSemSucata(row.sucata)) {
      return (
        <Typography variant="body1">Sem sucata</Typography>
      );
    }

    const peso = formatPesoKg(row.sucata?.pesoTotalKg);
    const quantidade = row.sucata?.quantidadeItens;

    return (
      <>
        {peso && <StatusLabelValue label="Peso" value={peso} />}
        {kind === USO_MUTUO_ROW_KIND.PESAGEM_SUCATA && quantidade != null && (
          <Typography variant="body1" sx={{ mt: peso ? 0.25 : 0 }}>
            {quantidade === 1 ? '1 material' : `${quantidade} materiais`}
          </Typography>
        )}
      </>
    );
  }

  if (isMarcadorRow(kind)) {
    return <EmptyCell />;
  }

  return null;
};

export const renderUsoMutuoStatusDigitacao = (row) => {
  const digitadoEmValue = getDigitadoEmValue(row);
  const subNsmpStatus = getSubNsmpStatus(row);
  const status = getStatusDigitacaoLabel(row);

  return (
    <>
      <Typography variant="body2">{status}</Typography>
      {digitadoEmValue && (
        <>
          <Typography variant="caption" fontWeight={700}>Digitado em: </Typography>
          <Typography variant="caption">{digitadoEmValue}</Typography>
        </>
      )}
      {subNsmpStatus && (
        <>
          <br />
          <Typography variant="caption" fontWeight={700}>Total: </Typography>
          <Typography variant="caption">{subNsmpStatus.total}</Typography>
          <br />
          <Typography variant="caption" fontWeight={700}>Aguardando Validação: </Typography>
          <Typography variant="caption">{subNsmpStatus.aguardandoValidacao}</Typography>
          <br />
          <Typography variant="caption" fontWeight={700}>Válidos: </Typography>
          <Typography variant="caption">{subNsmpStatus.validado}</Typography>
          <br />
          <Typography variant="caption" fontWeight={700}>Inválidos: </Typography>
          <Typography variant="caption">{subNsmpStatus.invalidado}</Typography>
        </>
      )}
    </>
  );
};
